import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, doc, updateDoc, increment, arrayUnion } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { 
  FaMapMarkerAlt, FaMedal, FaStar, FaUserFriends, FaBed, FaCheckCircle, 
  FaEye, FaHandPointer, FaHome, FaFilter 
} from 'react-icons/fa';
import { Gem, ShieldCheck, Compass } from "lucide-react";
import { db, storage } from './firebase';
import { getAuth } from 'firebase/auth';
import './PGListing.css';
import './LoadingScreen.css';
import apnagharLogo from './apnaghar.png';
import { Helmet } from 'react-helmet';
// ----------------- Loading Screen -----------------
function LoadingScreen({ loading }) {
  if (!loading) return null;
  return (
    <div className="loading-screen">
      <div className="loader"></div>
      <p>Please wait, we are getting your houses ready...</p>
    </div>
  );
}


// ----------------- Filter Dropdown -----------------
function FilterDropdown({ filters, setFilters, onApply, onReset, onClose }) {
  return (
    <div className="filter-dropdown">
      <button className="filter-close-btn" onClick={onClose}>×</button>
      <div className="filter-group">
        <label>Price Range (₹/month)</label>
        <div className="price-inputs">
          <input
            type="number"
            placeholder="Min"
            value={filters.minRent}
            onChange={(e) => setFilters(f => ({ ...f, minRent: e.target.value }))}
          />
          <span>–</span>
          <input
            type="number"
            placeholder="Max"
            value={filters.maxRent}
            onChange={(e) => setFilters(f => ({ ...f, maxRent: e.target.value }))}
          />
        </div>
      </div>

      <div className="filter-group">
        <label>Occupancy</label>
        <select
          value={filters.occupancy}
          onChange={(e) => setFilters(f => ({ ...f, occupancy: e.target.value }))}
        >
          <option value="">Any</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
          <option value="Co-living">Co-living</option>
        </select>
      </div>

      <div className="filter-group">
        <label>Room Type</label>
        <select
          value={filters.roomType}
          onChange={(e) => setFilters(f => ({ ...f, roomType: e.target.value }))}
        >
          <option value="">Any</option>
          <option value="Single">Single</option>
          <option value="Double">Double</option>
          <option value="Triple">Triple</option>
        </select>
      </div>

      <div className="filter-group">
        <label>City</label>
        <select
          value={filters.city}
          onChange={(e) => setFilters(f => ({ ...f, city: e.target.value }))}
        >
          <option value="">All (Assam)</option>
          <option value="Jorhat">Jorhat</option>
          <option value="Dergaon">Dergaon</option>
          <option value="Golaghat">Golaghat</option>
          {/* Add more cities as per your house-listings data */}
        </select>
      </div>

      <div className="filter-actions">
        <button className="apply-btn" onClick={onApply}>Apply</button>
        <button className="reset-btn" onClick={onReset}>Reset</button>
      </div>
    </div>
  );
}

// ----------------- PG Section Component -----------------
function PGSection({ title, data, routeType, navigate, handleCardClick }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasScrolled, setHasScrolled] = useState(false);
  const sliderRef = useRef(null);



  const handleScroll = () => {
    if (!sliderRef.current) return;
    const scrollLeft = sliderRef.current.scrollLeft;
    const cardWidth = sliderRef.current.firstChild?.offsetWidth + 12 || 0;
    const index = Math.round(scrollLeft / cardWidth);
    setCurrentIndex(index);
    if (!hasScrolled) setHasScrolled(true);
  };

  useEffect(() => {
    const sliders = document.querySelectorAll(".road-slider");
    sliders.forEach((slider) => {
      let isDown = false;
      let startX;
      let scrollLeft;

      slider.addEventListener("mousedown", (e) => {
        isDown = true;
        slider.classList.add("active");
        startX = e.pageX - slider.offsetLeft;
        scrollLeft = slider.scrollLeft;
      });

      slider.addEventListener("mouseleave", () => {
        isDown = false;
        slider.classList.remove("active");
      });

      slider.addEventListener("mouseup", () => {
        isDown = false;
        slider.classList.remove("active");
      });

      slider.addEventListener("mousemove", (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - slider.offsetLeft;
        const walk = (x - startX) * 1.5;
        slider.scrollLeft = scrollLeft - walk;
      });
    });
  }, []);

  return (
    <div className="pg-section-block">
      <div className="section-header">
        <h2>{title}</h2>
        <button className="show-more-btn" onClick={() => navigate(`/pg-list/${routeType}`)}>Show More →</button>
      </div>

      <div className="pg-slider-wrapper">
        {!hasScrolled && data.length > 1 && (
          <div className="swipe-hint">
            <FaHandPointer className="hand-icon animated-hand" /> Swipe →
          </div>
        )}

        <div className="pg-slider" ref={sliderRef} onScroll={handleScroll}>
          {data.map(pg => {
            const isPremium = pg.isPremium;
            const isVerified = pg.isVerified;

            return (
              <div
                key={pg.id}
                className={`pg-card-wrapper ${isPremium ? 'premium-card-wrapper' : ''} ${pg.showBooked ? 'booked-card' : ''}`}
                onClick={() => !pg.showBooked && handleCardClick(pg)}
              >
                {pg.showBooked && (
                  <div className="booked-overlay">
                    <div className="booked-text">🏠 Booked</div>
                    <p className="booked-subtext">No rooms available</p>
                  </div>
                )}

                {isPremium && <div className="premium-badge"><FaMedal /> Featured</div>}

                <div className={`pg-card ${isPremium ? 'premium-card' : ''} ${isVerified && !isPremium ? 'verified-border' : ''}`}>
                  <img
                    src={pg.thumbnailURL || 'https://dummyimage.com/400x300/cccccc/000000&text=No+Image'}
                    alt={pg.pgName || 'PG Image'}
                    className="pg-img"
                  />
                  <div className="pg-content">
                    <h3>{pg.pgName}{isVerified && <FaCheckCircle className="verified-tick" />}</h3>
                    <p><FaMapMarkerAlt /> {pg.city}, {pg.state}</p>
                    <p><FaUserFriends /> {pg.occupancy || 'N/A'}</p>
                    <p><FaBed /> {pg.roomType || 'N/A'}</p>
                    <p className="price">₹ {pg.rent || 'N/A'}/month</p>
                    <p className="listing-id">Listing ID: <span>{pg.id}</span></p>
                    
                    {/* Nearby Roads */}
                    {pg.nearbyRoads && pg.nearbyRoads.length > 0 && (
                      <div className="road-slider">
                        {pg.nearbyRoads.map((r, idx) => (
                          <div key={idx} className="road-info">
                            <FaMapMarkerAlt className="road-icon" />
                            <span>{r.name} • {r.distance}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <p className="rating">
                      {[1,2,3,4,5].map(n => <FaStar key={n} color={'#d0d0d0'} />)}
                      {pg.views > 0 && <> | <FaEye /> {pg.views}</>}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="slider-dots">
          {data.map((_, i) => (
            <span key={i} className={`dot ${i === currentIndex ? 'active' : ''}`}></span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ----------------- PG Listing Component -----------------
function PGListing() {
  const [pgList, setPgList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({
    minRent: "",
    maxRent: "",
    occupancy: "",
    roomType: "",
    city: ""
  });
const [showSignInModal, setShowSignInModal] = useState(false);

  const navigate = useNavigate();
  const auth = getAuth();
const seoTitle = "Discover PG Accommodations Across Assam | ApkaGhr";
const seoDescription = "Find premium, verified, and budget-friendly PG accommodations in Assam. ApkaGhr offers trusted listings with detailed info, nearby locations, and instant booking.";
const seoUrl = window.location.href; 
const seoImage = apnagharLogo; 

  // Fetch PG listings directly from Firestore
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'house-listings'), async (snapshot) => {
      setLoading(true);

      const dataWithDetails = await Promise.all(
        snapshot.docs.map(async docSnap => {
          const pg = { id: docSnap.id, ...docSnap.data() };

          // ✅ Fetch thumbnail from storage if available
          let thumbnailURL = '';
          if (pg.thumbnailPath) {
            try { 
              thumbnailURL = await getDownloadURL(ref(storage, pg.thumbnailPath)); 
            } catch (err) { console.warn("Thumbnail not found", err); }
          }

          // ✅ Use city, state, and nearbyRoads directly from Firestore
          pg.city = pg.city || "Assam";
          pg.state = pg.state || "Assam";
          pg.nearbyRoads = Array.isArray(pg.nearbyRoads) ? pg.nearbyRoads : [];

          return { 
            ...pg, 
            thumbnailURL, 
            views: pg.views || 0, 
            viewedBy: pg.viewedBy || [],
            showBooked: pg.showBooked || false 
          };
        })
      );

      setPgList(dataWithDetails);
      setLoading(false);
    });

    return () => unsub();
  }, []);

 const handleCardClick = async (pg) => {
  const user = auth.currentUser;

  if (!user) {
    setShowSignInModal(true); // Show modal
    return; // Stop navigation
  }

  try {
    if (!(pg.viewedBy || []).includes(user.uid)) {
      const pgRef = doc(db, "house-listings", pg.id);
      await updateDoc(pgRef, {
        views: increment(1),
        viewedBy: arrayUnion(user.uid)
      });
    }
  } catch (err) {
    console.error("Error updating views:", err);
  }

  navigate(`/pg/${pg.id}`);
};

  const filteredPGs = useMemo(() => {
    return pgList.filter(pg => (
      (!filters.city || pg.city?.toLowerCase().includes(filters.city.toLowerCase())) &&
      (!filters.occupancy || pg.occupancy === filters.occupancy) &&
      (!filters.roomType || pg.roomType === filters.roomType) &&
      (!filters.minRent || pg.rent >= Number(filters.minRent)) &&
      (!filters.maxRent || pg.rent <= Number(filters.maxRent))
    ));
  }, [pgList, filters]);

  const premiumPGs = pgList.filter(pg => pg.isPremium);
  const verifiedPGs = pgList.filter(pg => pg.isVerified && !pg.isPremium);
  const allPGs = pgList;

  return (
    <div className="pg-section">
      <LoadingScreen loading={loading} />
      <Helmet>
  <title>{seoTitle}</title>
  <meta name="description" content={seoDescription} />
  <meta name="robots" content="index, follow" />
  <link rel="canonical" href={seoUrl} />

  {/* Open Graph / Social Meta */}
  <meta property="og:title" content={seoTitle} />
  <meta property="og:description" content={seoDescription} />
  <meta property="og:type" content="website" />
  <meta property="og:url" content={seoUrl} />
  <meta property="og:image" content={seoImage} />

  {/* Twitter Card */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content={seoTitle} />
  <meta name="twitter:description" content={seoDescription} />
  <meta name="twitter:image" content={seoImage} />

  {/* Structured data for PG listings */}
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "PG Listings in Assam",
      "url": seoUrl,
      "itemListElement": pgList.map((pg, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": `${window.location.origin}/pg/${pg.id}`,
        "name": pg.pgName,
        "image": pg.thumbnailURL || seoImage,
        "description": `PG in ${pg.city}, ${pg.state} - ${pg.roomType}, ${pg.occupancy}, ₹${pg.rent}/month`,
      }))
    })}
  </script>
</Helmet>

      <div className="pg-container" style={{ display: loading ? 'none' : 'block' }}>
        <div className="pg-brand-header">
          <img src={apnagharLogo} alt="Logo" className="pg-logo" />
          <h1 className="pg-brand-name">ApkaGhr</h1>
        </div>
        <button onClick={() => navigate('/homepage')} className="back-btnwhy">← Back</button>
        <h2 className="pg-heading">
          <FaHome className="pg-heading-icon" /> Discover PG Accommodations Across Assam
        </h2>
        <p className="pg-subheading">
          <FaStar className="pg-sub-icon" /> ApkaGhr brings you a trusted platform to explore Paying Guest (PG) options in Assam. <br />
          <FaCheckCircle className="pg-sub-icon" /> From premium stays to verified listings and budget-friendly accommodations, find the perfect home that matches your lifestyle and budget.
        </p>

        <div className="search-wrapper">
          <button className="filter-btn-only" onClick={() => setShowFilter(!showFilter)}>
            <FaFilter /> Filter
          </button>
        </div>

        {showFilter && (
          <FilterDropdown
            filters={filters}
            setFilters={setFilters}
            onApply={() => {
              setShowFilter(false);
              navigate('/search-results', { state: { filteredPGs } });
            }}
            onReset={() => setFilters({ minRent: "", maxRent: "", occupancy: "", roomType: "", city: "" })}
            onClose={() => setShowFilter(false)}
          />
        )}
{showSignInModal && (
  <div className="modal-overlay">
    <div className="modal-content">
      <button className="modal-close-btn" onClick={() => setShowSignInModal(false)}>×</button>
      <h3>Sign In Required</h3>
      <p>Please sign in to view this property.</p>
      <div className="modal-actions">
        <button onClick={() => navigate('/signin')} className="sign-in-btn">Sign In</button>
      </div>
    </div>
  </div>
)}


        <PGSection title={<><Gem /> Premium Listings</>} data={premiumPGs.slice(0,6)} routeType="featured" navigate={navigate} handleCardClick={handleCardClick} />
        <PGSection title={<><ShieldCheck /> Verified Listings</>} data={verifiedPGs.slice(0,6)} routeType="verified" navigate={navigate} handleCardClick={handleCardClick} />
        <PGSection title={<><Compass /> All PG Listings</>} data={allPGs.slice(0,6)} routeType="basic" navigate={navigate} handleCardClick={handleCardClick} />
      </div>
    </div>
  );
}

export default PGListing;
